import { Button, Divider, Drawer, Stack, Typography } from '@mui/material'
import {
  type ResponseNumber,
  type ResponseNumberDecimalPoint,
  ResponseNumberSubTypeEnum,
  RuleDefTypeEnum,
  type RuleVariableDef,
  type TemplateNodeSchema,
  type TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import { type FormikHelpers, useFormik } from 'formik'
import type { FC } from 'react'
import {
  isNumberRuleOutOfRange,
  numberRuleOutOfRangeMessage,
} from 'src/constants/responseNumber'
import { spreadRuleVariableDefs } from '../question'
import FractionalDigitsRow, {
  MAX_NUMBER_FRACTIONAL_DIGITS,
} from './FractionalDigitsRow'
import NormalValueRow from './NormalValueRow'

interface OwnProps {
  isOpen: boolean
  questionNode: TemplateNodeSchema
  response?: ResponseNumber
  onClose: () => void
  onSave: (questionPatch: Partial<TemplateQuestion>) => void
}

export type NumberRuleProps = {
  hasNumberRule: boolean
  minimum?: number
  maximum?: number
  scale?: string
  subType?: ResponseNumberSubTypeEnum
  decimalPoint?: ResponseNumberDecimalPoint
  ruleDefType?: RuleDefTypeEnum
  minimumRuleVariableDef?: RuleVariableDef
  maximumRuleVariableDef?: RuleVariableDef
}

const EditResponseNumberRuleEnableProductMasterDrawerContainer: FC<
  OwnProps
> = ({ response, questionNode, onClose, isOpen, onSave }) => {
  // 数値タイプの質問にルールを設定する
  const changeResponseNumberRule = (
    scale: string,
    newResponseNumber: Pick<
      ResponseNumber,
      | 'minimum'
      | 'maximum'
      | 'subType'
      | 'decimalPoint'
      | 'ruleDefType'
      | 'ruleVariableDefs'
    >,
  ) => {
    onSave({ scale, responseNumbers: [newResponseNumber] })
  }

  // subType が 0 である場合にもルールなしの判定としたいが、
  // 0 は Enum に定義されておらず型エラーが発生するため、falsy かどうかで判定する
  const hasNumberRule = !!response?.subType

  const initialValues: NumberRuleProps = {
    hasNumberRule,
    minimum: response?.minimum ?? undefined,
    maximum: response?.maximum ?? undefined,
    scale: questionNode.question?.scale,
    subType: response?.subType,
    decimalPoint: {
      value: response?.decimalPoint?.value,
      isActive: response?.decimalPoint?.isActive,
    },
    ruleDefType: response?.ruleDefType,
    ...spreadRuleVariableDefs(response?.ruleVariableDefs),
  }

  const formikProps = useFormik({
    initialValues: initialValues,
    validate: ({
      hasNumberRule,
      subType,
      minimum,
      maximum,
      ruleDefType,
      minimumRuleVariableDef,
      maximumRuleVariableDef,
      decimalPoint,
    }: NumberRuleProps) => {
      const errors: Partial<Record<keyof NumberRuleProps, string>> = {}

      if (hasNumberRule) {
        if (!ruleDefType) {
          errors.ruleDefType = '設定方法を選択してください'
          return errors
        }
        if (!subType) {
          errors.subType = '範囲を選択してください'
          return errors
        }

        switch (ruleDefType) {
          case RuleDefTypeEnum.Master:
            switch (subType) {
              case ResponseNumberSubTypeEnum.BETWEEN:
                if (minimumRuleVariableDef == null) {
                  errors.minimumRuleVariableDef = '最小値を選択してください'
                  return errors
                }
                if (maximumRuleVariableDef == null) {
                  errors.maximumRuleVariableDef = '最大値を選択してください'
                  return errors
                }
                break
              case ResponseNumberSubTypeEnum.LESS_THAN:
                if (maximumRuleVariableDef == null) {
                  errors.maximumRuleVariableDef = '最大値を選択してください'
                  return errors
                }
                break
              case ResponseNumberSubTypeEnum.GREATER_THAN:
                if (minimumRuleVariableDef == null) {
                  errors.minimumRuleVariableDef = '最小値を選択してください'
                  return errors
                }
                break
            }
            break
          case RuleDefTypeEnum.Fixed:
            switch (subType) {
              case ResponseNumberSubTypeEnum.BETWEEN:
                if (minimum === undefined || minimum === null) {
                  errors.minimum = '最小値を入力してください'
                  return errors
                }
                if (Number.isNaN(minimum)) {
                  errors.minimum = '最小値を数値で入力してください'
                  return errors
                }
                if (isNumberRuleOutOfRange(minimum)) {
                  errors.minimum = numberRuleOutOfRangeMessage('最小値')
                  return errors
                }
                if (maximum === undefined || maximum === null) {
                  errors.maximum = '最大値を入力してください'
                  return errors
                }
                if (Number.isNaN(maximum)) {
                  errors.maximum = '最大値を数値で入力してください'
                  return errors
                }
                if (isNumberRuleOutOfRange(maximum)) {
                  errors.maximum = numberRuleOutOfRangeMessage('最大値')
                  return errors
                }
                if (minimum > maximum) {
                  errors.minimum = '最小値 ー 最大値 で入力してください'
                  return errors
                }
                break
              case ResponseNumberSubTypeEnum.LESS_THAN:
                if (maximum === undefined || maximum === null) {
                  errors.maximum = '最大値を入力してください'
                  return errors
                }
                if (Number.isNaN(maximum)) {
                  errors.maximum = '最大値を数値で入力してください'
                  return errors
                }
                if (isNumberRuleOutOfRange(maximum)) {
                  errors.maximum = numberRuleOutOfRangeMessage('最大値')
                  return errors
                }
                break
              case ResponseNumberSubTypeEnum.GREATER_THAN:
                if (minimum === undefined || minimum === null) {
                  errors.minimum = '最小値を入力してください'
                  return errors
                }
                if (Number.isNaN(minimum)) {
                  errors.minimum = '最小値を数値で入力してください'
                  return errors
                }
                if (isNumberRuleOutOfRange(minimum)) {
                  errors.minimum = numberRuleOutOfRangeMessage('最小値')
                  return errors
                }
                break
            }
            break
        }
      }

      if (decimalPoint) {
        const { isActive, value } = decimalPoint
        if (isActive && value === undefined) {
          errors.decimalPoint = '小数点以下の桁数を入力してください。'
          return errors
        }
        if (
          isActive &&
          value !== undefined &&
          value > MAX_NUMBER_FRACTIONAL_DIGITS
        ) {
          errors.decimalPoint = `小数点以下の桁数は${MAX_NUMBER_FRACTIONAL_DIGITS}以下で入力してください。`
          return errors
        }
        if (!!subType && isActive && value !== undefined) {
          const maximumDecimalPoint = String(maximum).split('.')[1]?.length || 0
          const minimumDecimalPoint = String(minimum).split('.')[1]?.length || 0
          const ruleDecimalPoint =
            maximumDecimalPoint > minimumDecimalPoint
              ? maximumDecimalPoint
              : minimumDecimalPoint
          if (value < ruleDecimalPoint) {
            errors.decimalPoint =
              '小数点以下の桁数をルールの小数点桁数より大きい数値で入力してください。'
            return errors
          }
        }
      }

      return errors
    },
    onSubmit: (
      {
        minimum,
        maximum,
        subType,
        decimalPoint,
        scale,
        ruleDefType,
        minimumRuleVariableDef,
        maximumRuleVariableDef,
      }: NumberRuleProps,
      { resetForm }: FormikHelpers<NumberRuleProps>,
    ) => {
      const newResponseNumber = buildNewResponseNumber({
        minimum,
        maximum,
        subType,
        decimalPoint,
        ruleDefType,
        minimumRuleVariableDef,
        maximumRuleVariableDef,
      })
      changeResponseNumberRule(scale ?? '', newResponseNumber)
      onClose()
      resetForm()
    },
    enableReinitialize: true,
  })

  const { handleSubmit, resetForm } = formikProps

  const onCloseOrCancel = () => {
    onClose()
    resetForm()
  }

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return
        onCloseOrCancel()
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: '100%',
            width: '100%',
            maxWidth: 608,
          },
        },
      }}
    >
      <Stack sx={{ minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ my: 2, px: 3 }}>
          ルールの設定
        </Typography>
        <Divider />
        <Stack sx={{ p: 2 }} spacing={2}>
          <NormalValueRow {...{ formikProps }} />
          <FractionalDigitsRow {...{ formikProps }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="text" onClick={onCloseOrCancel}>
              キャンセル
            </Button>
            <Button variant="contained" onClick={() => handleSubmit()}>
              保存
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default EditResponseNumberRuleEnableProductMasterDrawerContainer

const buildNewResponseNumber = ({
  minimum,
  maximum,
  subType,
  decimalPoint,
  ruleDefType,
  minimumRuleVariableDef,
  maximumRuleVariableDef,
}: Omit<NumberRuleProps, 'hasNumberRule'>): ResponseNumber => {
  const base = {
    ruleDefType,
    subType,
    decimalPoint,
  }

  switch (ruleDefType) {
    case RuleDefTypeEnum.Master:
      switch (subType) {
        case ResponseNumberSubTypeEnum.BETWEEN:
          return {
            ...base,
            minimum: undefined,
            maximum: undefined,
            ruleVariableDefs: [
              minimumRuleVariableDef,
              maximumRuleVariableDef,
            ].filter((v): v is RuleVariableDef => v != null),
          }
        case ResponseNumberSubTypeEnum.LESS_THAN:
          return {
            ...base,
            minimum: undefined,
            maximum: undefined,
            ruleVariableDefs: [maximumRuleVariableDef].filter(
              (v): v is RuleVariableDef => v != null,
            ),
          }
        case ResponseNumberSubTypeEnum.GREATER_THAN:
          return {
            ...base,
            minimum: undefined,
            maximum: undefined,
            ruleVariableDefs: [minimumRuleVariableDef].filter(
              (v): v is RuleVariableDef => v != null,
            ),
          }
      }
      break
    case RuleDefTypeEnum.Fixed:
      switch (subType) {
        case ResponseNumberSubTypeEnum.BETWEEN:
          return {
            ...base,
            minimum,
            maximum,
            ruleVariableDefs: undefined,
          }
        case ResponseNumberSubTypeEnum.LESS_THAN:
          return {
            ...base,
            minimum: undefined,
            maximum,
            ruleVariableDefs: undefined,
          }
        case ResponseNumberSubTypeEnum.GREATER_THAN:
          return {
            ...base,
            minimum,
            maximum: undefined,
            ruleVariableDefs: undefined,
          }
      }
      break
  }
  return {
    minimum: undefined,
    maximum: undefined,
    subType: undefined,
    decimalPoint,
    ruleDefType: undefined,
    ruleVariableDefs: undefined,
  }
}
